import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';

export interface RouteMetaData {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SeoMetaService {
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  initialize(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.getRouteMetaData()),
      )
      .subscribe((metaData) => {
        if (metaData) {
          this.updateMetaTags(metaData);
        }
      });
  }

  private getRouteMetaData(): RouteMetaData | null {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.data['seo'] || null;
  }

  private updateMetaTags(metaData: RouteMetaData): void {
    if (metaData.title) {
      this.title.setTitle(metaData.title);
    }

    if (metaData.description) {
      this.meta.updateTag({
        name: 'description',
        content: metaData.description,
      });
    }

    if (metaData.ogTitle) {
      this.meta.updateTag({
        property: 'og:title',
        content: metaData.ogTitle,
      });
    }
    if (metaData.ogDescription) {
      this.meta.updateTag({
        property: 'og:description',
        content: metaData.ogDescription,
      });
    }
    if (metaData.ogImage) {
      this.meta.updateTag({
        property: 'og:image',
        content: metaData.ogImage,
      });
    }
    if (metaData.ogUrl) {
      this.meta.updateTag({ property: 'og:url', content: metaData.ogUrl });
    }

    if (metaData.twitterTitle) {
      this.meta.updateTag({
        name: 'twitter:title',
        content: metaData.twitterTitle,
      });
    }
    if (metaData.twitterDescription) {
      this.meta.updateTag({
        name: 'twitter:description',
        content: metaData.twitterDescription,
      });
    }
    if (metaData.twitterImage) {
      this.meta.updateTag({
        name: 'twitter:image',
        content: metaData.twitterImage,
      });
    }
  }
}
